import React from 'react';

import { ConsoleShell } from '../../../design-system/components/shells';
import {
  Button,
  Drawer,
  PageHeader,
  Pagination,
  SwitchRow,
  Table,
  Toolbar,
} from '../../../design-system/components/desktop';
import {
  ActionRow,
  ConfirmDialog,
  ConsoleCell,
  ConsoleDenied,
  ConsoleEmpty,
  ConsoleError,
  ConsoleStack,
  DetailList,
  DetailRow,
  DetailSection,
  Notice,
  ReasonBlock,
  RejectReasonForm,
  ResolvedNote,
  StatusPill,
} from '../../../design-system/patterns/console';
import { formatCurrency } from '../../../utils/common';

/**
 * Seller applications, the view. Pure.
 *
 * WHAT DIED HERE
 * --------------
 * - Lines 1 to 475 of the old `AdminSellers.jsx` were a commented-out earlier
 *   version of the same page. Deleted.
 * - `Pagination.jsx`, which injected a raw `<style>` block into the document
 *   with `color: #fff` hardcoded on both the buttons and the page indicator, so
 *   the control was white-on-white and invisible in light theme. Deleted, and
 *   with it the `2`-suffixed twin that `/market-dashboard` carried purely to
 *   dodge the class collision between the two copies.
 * - `FIlters.jsx`, which was imported and never rendered.
 * - `data.table.jsx` and its hand-rolled skeleton.
 * - `components/UI/SellerDetailsCard/`, a 332-line panel with a gradient
 *   approve button, a red reject button, a hand-rolled toggle switch and a
 *   hardcoded `R89.99` "Payable" figure that was never read from anything.
 *
 * THE PAYABLE FIGURE IS NOT REPLACED
 * ----------------------------------
 * `R89.99` was a literal. There is no payable field on the seller profile, so
 * inventing a replacement would be repeating the mistake in a nicer typeface.
 * The wallet section renders the seller's own price only, and the drawer says
 * nothing it cannot source.
 *
 * DESTRUCTIVE COMMANDS
 * --------------------
 * Reject is a GHOST button whose label carries the consequence, plus a real
 * reason form that does the work. There is no `.dsk-btn--danger` in this system
 * and no red button is emitted anywhere on this page.
 *
 * NO STATUS FILTER, DELIBERATELY
 * ------------------------------
 * The dead `FIlters.jsx` offered one, and the upstream `admin-queue.html`
 * template has one, but `sellarModule.getSellarProfiles` only forwards unknown
 * params into the query string and nothing confirms the backend honours a
 * `status`. A filter that silently returns the unfiltered list is worse than no
 * filter on a moderation queue, so this ships with search and pagination only
 * until the parameter is confirmed. Search IS confirmed: it was already wired.
 *
 * ONE OVERLAY AT A TIME
 * ---------------------
 * Opening the confirmation or the reason form CLOSES the drawer, and cancelling
 * either one brings it back. That is the upstream template's rule verbatim:
 * "the drawer steps aside so the reason form is the only thing asking for a
 * decision". It is not only a taste call. `Drawer` and `Modal` share
 * `--ds-z-modal` and both install a focus trap through `useModalLayer`, so two
 * open at once means two traps fighting over Tab and an Escape that closes both.
 * `open` is derived, not a second piece of state, so the two cannot disagree.
 */

function initialsFor(seller) {
  const first = seller?.first_name || seller?.user?.full_name || seller?.store_name || '';
  const last = seller?.last_name || '';
  return `${first.charAt(0)}${last.charAt(0)}`.trim() || first.slice(0, 2);
}

function fullNameFor(seller) {
  const joined = `${seller?.first_name || ''} ${seller?.last_name || ''}`.trim();
  return joined || seller?.user?.full_name || 'Unnamed applicant';
}

export function AdminSellersView({
  sellers = [],
  loading = false,
  error,
  onRetry,
  denied = false,

  search = '',
  onSearchChange,

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  selected,
  onOpen,
  onClose,

  onApprove,
  onReject,
  onAutoApproveChange,
  onAvailabilityChange,
  autoApprove = false,
  autoApproveBusy = false,
  unavailable = false,
  availabilityBusy = false,
  actionBusy = false,

  confirmApproveOpen = false,
  onConfirmApproveOpen,
  onConfirmApproveClose,
  rejectOpen = false,
  onRejectOpen,
  onRejectClose,
}) {
  const columns = [
    {
      key: 'store_name',
      header: 'Store',
      render: (row) => (
        <ConsoleCell
          title={row.store_name || 'No store name'}
          sub={row.user?.email}
          initials={initialsFor(row)}
        />
      ),
    },
    { key: 'applicant', header: 'Applicant', render: (row) => fullNameFor(row) },
    {
      key: 'location',
      header: 'Location',
      muted: true,
      render: (row) => [row.city, row.province].filter(Boolean).join(', ') || 'Not supplied',
    },
    {
      key: 'created_at',
      header: 'Submitted',
      muted: true,
      render: (row) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString('en-ZA') : 'Unknown',
    },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
  ];

  const shell = (children) => (
    <ConsoleShell
      variant="admin"
      title="Sellers"
      breadcrumbs={[{ label: 'People' }, { label: 'Sellers' }]}
      showSearch={false}
    >
      {children}
    </ConsoleShell>
  );

  if (denied) return shell(<ConsoleDenied />);

  const filtered = Boolean(search);
  const isApproved = String(selected?.status || '').toLowerCase() === 'approved';
  const isPending = String(selected?.status || '').toLowerCase() === 'pending';
  const isRejected = String(selected?.status || '').toLowerCase() === 'rejected';

  return shell(
    <>
      <ConsoleStack>
        <PageHeader
          title="Seller applications"
          sub={
            loading
              ? 'Loading applications'
              : `${sellers.length} shown of ${total} ${total === 1 ? 'application' : 'applications'}`
          }
        />

        <Table
          columns={columns}
          data={error ? [] : sellers}
          rowKey="id"
          loading={loading}
          onRowClick={(row) => onOpen(row)}
          emptyState={
            error ? (
              <ConsoleError error={error} onRetry={onRetry} />
            ) : (
              <ConsoleEmpty
                filtered={filtered}
                illustration="no-applications-yet"
                title="No seller applications yet"
                text="Applications appear here as sellers complete registration."
                filteredText="No application matches that search. Clear it to see every application again."
                onClear={() => onSearchChange && onSearchChange('')}
              />
            )
          }
          rowActions={(row) => (
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onOpen(row);
              }}
            >
              Review
            </Button>
          )}
          toolbar={
            <Toolbar
              search={search}
              onSearchChange={onSearchChange}
              searchPlaceholder="Filter by store or applicant"
              searchLabel="Filter seller applications"
            />
          }
          pagination={
            pageCount > 1 ? (
              <Pagination
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                totalItems={total}
                itemNoun="applications"
                onPageChange={onPageChange}
              />
            ) : null
          }
        />
      </ConsoleStack>

      <Drawer
        open={Boolean(selected) && !confirmApproveOpen && !rejectOpen}
        onClose={onClose}
        kicker="Seller application"
        title={selected?.store_name || fullNameFor(selected)}
        footer={
          isPending ? (
            <ActionRow>
              <Button variant="ghost" onClick={onRejectOpen} disabled={actionBusy}>
                Reject application
              </Button>
              <Button variant="primary" onClick={onConfirmApproveOpen} disabled={actionBusy}>
                Approve seller
              </Button>
            </ActionRow>
          ) : (
            <ResolvedNote>
              {isApproved
                ? 'This seller has been approved and can list cards.'
                : isRejected
                  ? 'This application was rejected.'
                  : 'No decision is pending on this application.'}
            </ResolvedNote>
          )
        }
      >
        {selected ? (
          <>
            <DetailSection>
              <DetailList>
                <DetailRow label="Applicant" value={fullNameFor(selected)} />
                <DetailRow label="Email" value={selected.user?.email} />
                <DetailRow
                  label="Role"
                  value={
                    selected.user?.role ? (
                      <span style={{ textTransform: 'capitalize' }}>{selected.user.role}</span>
                    ) : undefined
                  }
                />
                <DetailRow label="Status" value={<StatusPill status={selected.status} />} keepEmpty />
                <DetailRow
                  label="Address"
                  value={
                    [selected.address_line1, selected.address_line2, selected.city, selected.province]
                      .filter(Boolean)
                      .join(', ') || undefined
                  }
                />
                <DetailRow
                  label="Registered"
                  value={
                    selected.created_at
                      ? new Date(selected.created_at).toLocaleDateString('en-ZA')
                      : undefined
                  }
                />
                <DetailRow label="Listed price" value={formatCurrency(selected.price)} />
              </DetailList>
            </DetailSection>

            {(selected.rejection_reason || selected.remarks || selected.marks) && (
              <DetailSection>
                <ReasonBlock>
                  {selected.rejection_reason || selected.remarks || selected.marks}
                </ReasonBlock>
              </DetailSection>
            )}

            {/* Both switches commit immediately, which is why neither sits next
                to a save button. They are only offered on an approved seller,
                exactly as before: neither setting means anything on an
                application still waiting for a decision. */}
            {isApproved ? (
              <DetailSection title="Store controls">
                <SwitchRow
                  title="Auto-approval"
                  sub="New listings from this seller go live without manual review."
                  checked={autoApprove}
                  disabled={autoApproveBusy}
                  onChange={(event) => onAutoApproveChange(event.target.checked)}
                />
                <SwitchRow
                  title="Mark store unavailable"
                  sub="Buyers see an unavailability notice instead of the store."
                  checked={unavailable}
                  disabled={availabilityBusy}
                  onChange={(event) => onAvailabilityChange(event.target.checked)}
                />
              </DetailSection>
            ) : null}

            {isApproved && !selected.user?.email ? (
              <DetailSection>
                <Notice title="No contact email on file">
                  This seller cannot be reached about a listing decision.
                </Notice>
              </DetailSection>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirmApproveOpen}
        onClose={onConfirmApproveClose}
        onConfirm={onApprove}
        title="Approve seller"
        confirmLabel="Approve seller"
        busy={actionBusy}
      >
        <p>
          <strong>{selected?.store_name || fullNameFor(selected)}</strong> will be able to list
          cards on the marketplace immediately.
        </p>
      </ConfirmDialog>

      <RejectReasonForm
        open={rejectOpen}
        onClose={onRejectClose}
        onSubmit={onReject}
        title="Reject seller application"
        submitLabel="Reject application"
        initialReason={selected?.rejection_reason || selected?.remarks || selected?.marks || ''}
        busy={actionBusy}
      />
    </>,
  );
}

export default AdminSellersView;
